import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { authService, webauthnService } from '../services/api';

const AuthContext = createContext(null);

const STORAGE_KEY = 'od_user';
const TOKEN_KEY = 'od_token';
// Même clé que VehiclePreferenceModal.jsx — effacée à chaque connexion réelle
// (pas à l'inscription, pas à une simple restauration de session au
// rechargement de page) pour que ce modal réapparaisse à chaque fois que le
// client se connecte, y compris une reconnexion dans le même onglet.
const VEHICLE_PREF_SESSION_KEY = 'om_vehiclepref_seen_this_session';
const clearVehiclePrefSeen = () => { try { sessionStorage.removeItem(VEHICLE_PREF_SESSION_KEY); } catch {} };

// Avant : `err.response?.data?.error || fallback` retournait le même
// message générique ("Numéro ou mot de passe incorrect") que le serveur
// réponde vraiment 401 OU que la requête n'ait jamais atteint le serveur
// (backend éteint, coupure réseau) — l'utilisateur ne pouvait pas distinguer
// "mauvais mot de passe" de "serveur injoignable". err.response n'existe
// que si le serveur a effectivement répondu ; err.request existe sans
// réponse (timeout, requête envoyée mais rien reçu).
function getErrorMessage(err, fallback) {
  if (err.response) return err.response.data?.error || fallback;
  if (err.request) return 'Impossible de contacter le serveur. Vérifiez votre connexion.';
  return err.message || fallback;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) setUser(JSON.parse(stored));
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
    setLoading(false);
  }, []);

  const login = async (phone, password) => {
    const normalizedPhone = phone.replace(/[\s-]/g, '');
    try {
      const res = await authService.login({ phone: normalizedPhone, password });
      // Authentification 2FA (voir SecuriteSection.jsx) : mot de passe
      // correct mais le serveur n'a pas encore délivré de token — un vrai
      // code SMS vient d'être envoyé, à confirmer via verifyLoginTwoFactor.
      // Ne touche ni au localStorage ni à `user` : la session n'existe pas
      // encore tant que ce second facteur n'est pas vérifié.
      if (res.data.requiresTwoFactor) return { requiresTwoFactor: true };
      const { token, user: apiUser } = res.data;
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(apiUser));
      setUser(apiUser);
      clearVehiclePrefSeen();
      return apiUser;
    } catch (err) {
      const message = getErrorMessage(err, 'Numéro ou mot de passe incorrect');
      toast.error(message);
      throw new Error(message);
    }
  };

  // Connexion biométrique (WebAuthn réel — voir server/webauthn.js et
  // services/api.js) : aucun mot de passe, juste la vraie vérification Face
  // ID/empreinte/Windows Hello de l'appareil. `phone` est optionnel : omis,
  // le flux "sans identifiant" laisse l'appareil proposer directement les
  // comptes biométriques déjà enregistrés dessus.
  const loginWithBiometrics = async (phone) => {
    const normalizedPhone = phone ? phone.replace(/[\s-]/g, '') : undefined;
    try {
      const res = await webauthnService.loginWithBiometrics(normalizedPhone);
      const { token, user: apiUser } = res.data;
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(apiUser));
      setUser(apiUser);
      clearVehiclePrefSeen();
      return apiUser;
    } catch (err) {
      const message = err?.name === 'NotAllowedError' ? 'Vérification biométrique annulée' : getErrorMessage(err, 'Connexion biométrique impossible');
      throw new Error(message);
    }
  };

  // Confirme le second facteur SMS après un login() qui a répondu
  // requiresTwoFactor:true — même queue (localStorage + setUser) que login()
  // une fois le vrai code vérifié côté serveur (server/auth.js /login/verify-2fa).
  const verifyLoginTwoFactor = async (phone, code) => {
    const normalizedPhone = phone.replace(/[\s-]/g, '');
    try {
      const res = await authService.verifyLoginTwoFactor(normalizedPhone, code);
      const { token, user: apiUser } = res.data;
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(apiUser));
      setUser(apiUser);
      clearVehiclePrefSeen();
      return apiUser;
    } catch (err) {
      const message = getErrorMessage(err, 'Code incorrect');
      throw new Error(message);
    }
  };

  const register = async (userData) => {
    try {
      const res = await authService.register({
        phone: userData.phone.replace(/[\s-]/g, ''),
        name: userData.name,
        password: userData.password,
        role: userData.role || 'client',
      });
      const { token, user: apiUser } = res.data;
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(apiUser));
      setUser(apiUser);
      return apiUser;
    } catch (err) {
      const message = getErrorMessage(err, 'Inscription impossible');
      toast.error(message);
      throw new Error(message);
    }
  };

  // Dernière étape du "mot de passe oublié" — l'envoi/vérif du code SMS
  // (authService.requestPasswordReset/confirmPasswordResetOtp) se fait
  // directement depuis ForgotPasswordPage, comme sendOtp/verifyOtp le sont
  // déjà depuis RegisterPage. Ici seulement le changement effectif.
  const completePasswordReset = async (phone, newPassword) => {
    const normalizedPhone = phone.replace(/[\s-]/g, '');
    try {
      await authService.resetPassword(normalizedPhone, newPassword);
      return true;
    } catch (err) {
      const message = getErrorMessage(err, 'Impossible de réinitialiser le mot de passe');
      toast.error(message);
      throw new Error(message);
    }
  };

  // `silent` : le dashboard client (confirmLogout, DashboardContext) affiche
  // désormais une vraie page d'au revoir dédiée après déconnexion plutôt que
  // ce toast — les deux en même temps ferait doublon. Les autres points
  // d'appel (Navbar, DriverDashboard), qui n'ont pas cette page, gardent le
  // toast par défaut.
  const logout = ({ silent = false } = {}) => {
    const firstName = user?.name?.split(' ')[0];
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    if (silent) return firstName;
    // Même carte sombre/bordure bleue que le reste de l'app (voir TOAST_STYLE
    // dans les sections dashboard) — avant, ce toast utilisait le style par
    // défaut de react-hot-toast, seul endroit à trancher avec le reste.
    toast.success(firstName ? `À bientôt, ${firstName} !` : 'Déconnexion réussie', {
      icon: '👋',
      duration: 3000,
      style: { background: 'rgba(2,8,23,0.98)', color: '#fff', border: '1px solid rgba(59,130,246,0.3)' },
    });
    return firstName;
  };

  const updateUser = (updates) => {
    const updatedUser = { ...user, ...updates };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithBiometrics, verifyLoginTwoFactor, register, logout, updateUser, completePasswordReset }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
